import React from "react";
import { Box, Stack, Typography, Chip, Divider } from "@mui/material";
import { FiCalendar, FiRefreshCw, FiUser, FiFlag } from "react-icons/fi";
import dayjs from "dayjs";
import { recurrenceSummary, PRIORITY_OPTIONS, TASK_STATUS_OPTIONS } from "../../constants/recurrenceConstants";
import { useOccurrencePreview } from "../../hooks/useOccurrencePreview";

const T = {
  border: "#E2E8F0", text: "#0F172A", textSub: "#475569", textMuted: "#94A3B8",
  primary: "#0D9488", primaryLight: "#F0FDFA", surface: "#FFFFFF", bg: "#F8FAFC",
  shadow: { sm: "0 1px 4px rgba(15,23,42,0.07)" },
};

const Row = ({ icon: Icon, label, value }) => (
  <Stack direction="row" alignItems="flex-start" gap={1.5}>
    <Box sx={{ mt: "2px", color: T.textMuted, flexShrink: 0 }}>
      <Icon size={14} />
    </Box>
    <Box>
      <Typography sx={{ fontSize: "11px", color: T.textMuted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: "13px", color: T.text, fontWeight: 600, mt: "2px" }}>
        {value}
      </Typography>
    </Box>
  </Stack>
);

const StepReview = ({ values }) => {
  const priority    = PRIORITY_OPTIONS.find((p) => p.value === values.priority);
  const taskStatus  = TASK_STATUS_OPTIONS.find((s) => s.value === values.defaultStatus);
  const summary     = recurrenceSummary(values);
  const nextDates   = useOccurrencePreview(values, 5);

  const endLabel = (() => {
    if (values.recurrenceEndType === "on" && values.recurrenceEndDate)
      return `Until ${dayjs(values.recurrenceEndDate).format("D MMM YYYY")}`;
    if (values.recurrenceEndType === "after" && values.recurrenceEndAfter)
      return `After ${values.recurrenceEndAfter} occurrence${values.recurrenceEndAfter !== 1 ? "s" : ""}`;
    return "Never ends";
  })();

  return (
    <Stack spacing={2.5}>
      {/* Task summary card */}
      <Box sx={{ p: 2.5, borderRadius: "14px", border: `1px solid ${T.border}`, bgcolor: T.surface, boxShadow: T.shadow.sm }}>
        <Typography sx={{ fontSize: "16px", fontWeight: 800, color: T.text, mb: 2 }}>
          {values.title || "Untitled"}
        </Typography>
        {values.description && (
          <Typography sx={{ fontSize: "13px", color: T.textSub, mb: 2, lineHeight: 1.6 }}>
            {values.description}
          </Typography>
        )}

        <Stack spacing={1.5}>
          <Row icon={FiFlag}      label="Priority"      value={priority?.label   || "—"} />
          <Row icon={FiFlag}      label="Task status"   value={taskStatus?.label  || "To Do"} />
          {/* Always shown. Gating this on truthiness made sense while privacy
              was the exception; now it is the default, and the review step has
              to state the SHARED case too — that is the one worth noticing. */}
          <Row
            icon={FiUser}
            label="Assignee visibility"
            value={
              values.privateAssignees
                ? "Private — assignees can't see each other"
                : "Shared — assignees can see each other"
            }
          />
          {values.assignees?.length > 0 && (
            <Row
              icon={FiUser}
              label={values.assignees.length > 1 ? "Assignees" : "Assignee"}
              value={values.assignees.map((a) => a.label).filter(Boolean).join(", ") || "—"}
            />
          )}
          {values.support && (
            <Row icon={FiUser} label="Support" value={values.support?.label || "—"} />
          )}
          <Row icon={FiRefreshCw} label="Repeats"    value={summary} />
          <Row icon={FiCalendar}  label="Starts"     value={dayjs(values.recurringStartDate).format("D MMM YYYY")} />
          <Row icon={FiCalendar}  label="Ends"       value={endLabel} />
        </Stack>
      </Box>

      {/* Next 5 occurrences */}
      <Box>
        <Typography sx={{ fontSize: "12px", fontWeight: 700, color: T.textSub, mb: 1, textTransform: "uppercase", letterSpacing: "0.06em" }}>
          Next {nextDates.length} occurrence{nextDates.length !== 1 ? "s" : ""}
        </Typography>
        <Stack spacing={0.8}>
          {nextDates.length === 0 ? (
            <Typography sx={{ fontSize: "13px", color: T.textMuted, fontStyle: "italic" }}>
              No occurrences found — check your recurrence settings
            </Typography>
          ) : (
            nextDates.map((date, i) => (
              <Stack
                key={i}
                direction="row"
                alignItems="center"
                gap={1.5}
                sx={{
                  px: 2, py: 1,
                  borderRadius: "9px",
                  bgcolor: i === 0 ? T.primaryLight : T.bg,
                  border: `1px solid ${i === 0 ? T.primary + "30" : T.border}`,
                }}
              >
                <Box
                  sx={{
                    width: 6, height: 6, borderRadius: "50%",
                    bgcolor: i === 0 ? T.primary : T.textMuted,
                    flexShrink: 0,
                  }}
                />
                <Typography sx={{ fontSize: "13px", fontWeight: i === 0 ? 700 : 500, color: i === 0 ? T.primary : T.textSub }}>
                  {date.format("dddd, D MMM YYYY")}
                </Typography>
                {i === 0 && (
                  <Chip label="First" size="small" sx={{ height: 18, fontSize: "10px", fontWeight: 700, bgcolor: T.primary, color: "#fff", borderRadius: "5px" }} />
                )}
              </Stack>
            ))
          )}
        </Stack>
      </Box>
    </Stack>
  );
};

export default StepReview;
